import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface AvatarProps {
  photoUrl?: string | null;
  firstName?: string;
  lastName?: string;
  className?: string;
  initialsOverride?: string; // Optional: explicitly set initials (e.g., from short code when masked)
}

const AvatarContainer = styled.div`
  height: 80px;
  width: 80px;
  border-radius: 50%;
  background-color: var(--primary-blue, #0B4598); /* TempStars Cobalt Blue */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  overflow: hidden;
  flex-shrink: 0;
  
  @media (max-width: 465px) {
    font-size: 1.5rem; /* Smaller font on mobile to match smaller avatar */
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const sanitizeLetter = (s?: string) => (s || '').trim().replace(/^[^A-Za-z]*([A-Za-z]).*$/, '$1');

const extractFromOverride = (override?: string): string | null => {
  if (!override) return null;
  const letters = (override.match(/[A-Za-z]/g) || []).join('').toUpperCase();
  if (letters.length >= 2) return letters.slice(0, 2);
  if (letters.length === 1) return letters;
  return null;
};

const getInitials = (firstName?: string, lastName?: string, initialsOverride?: string): string => {
  // Prefer override (e.g., passed short code "H.B.") when available
  const fromOverride = extractFromOverride(initialsOverride);
  if (fromOverride) return fromOverride;

  // Otherwise derive from first and last names
  const firstInitial = sanitizeLetter(firstName);
  const lastInitial = sanitizeLetter(lastName);
  const combined = `${firstInitial}${lastInitial}`.toUpperCase();
  if (combined.length >= 2) return combined.slice(0, 2);
  if (combined.length === 1) return combined;
  // Rare edge case: both names and override missing. Use neutral placeholder to avoid a single arbitrary letter.
  return '';
};

const AvatarWithInitials: React.FC<AvatarProps> = ({ photoUrl, firstName, lastName, className, initialsOverride }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photoUrl]);

  if (photoUrl && !hasError) {
    return (
      <AvatarContainer className={className}>
        <ProfileImage
          src={photoUrl}
          alt={`${firstName} ${lastName}`}
          onError={() => setHasError(true)}
        />
      </AvatarContainer>
    );
  }

  return (
    <AvatarContainer className={className}>
      <span>{getInitials(firstName, lastName, initialsOverride)}</span>
    </AvatarContainer>
  );
};

export default AvatarWithInitials; 